import React, { lazy, Suspense } from 'react';
import { useFlag } from '@openfeature/react-sdk';
import NRLoadingPage from '../../pages/NRLoadingPage/NRLoadingPage';

const LazyNotFound = lazy(() => import('../../pages/NRNotFound/NRNotFound'));

/**
 * Reusable wrapper component that conditionally renders a route based on a feature flag.
 * If the feature flag is disabled, shows 404 page (or custom fallback).
 * 
 * @param {string} featureFlag - The feature flag key to check (e.g., FEATURE_FLAGS.NOTIFICATIONS)
 * @param {boolean} defaultValue - Default value if feature flag is not available (defaults to false)
 * @param {React.ReactNode} children - The component to render when feature flag is enabled
 * @param {React.ComponentType} fallback - Optional custom fallback component (defaults to 404 page)
 * 
 * @example
 * <FeatureFlagRouteWrapper featureFlag={FEATURE_FLAGS.NOTIFICATIONS}>
 *   <LazyNotifications />
 * </FeatureFlagRouteWrapper>
 */
function FeatureFlagRouteWrapper({ 
  featureFlag, 
  defaultValue = false, 
  children,
  fallback: FallbackComponent 
}) {
  const { value: isEnabled } = useFlag(featureFlag, defaultValue);

  if (!isEnabled) {
    if (FallbackComponent) {
      return (
        <Suspense fallback={<NRLoadingPage />}>
          <FallbackComponent />
        </Suspense>
      );
    }
    
    return (
      <Suspense fallback={<NRLoadingPage />}>
        <LazyNotFound />
      </Suspense>
    );
  }

  return <>{children}</>;
}

export default FeatureFlagRouteWrapper;

